import { hasAnyAccessToResource } from "api/general";
import { FormSection } from "components/FormControls/FormSection";
import {
  SimulationCampaign,
  SimulationEffect,
} from "components/Layout/Campaign/CampaignSimulation/types";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { LinkItem } from "LinkItem";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { SIMULATE_CAMPAIGN_LIST_ID } from "state/tables/types";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

const filterCampaignsWithUniqueID = (
  campaign: SimulationEffect,
  index: number,
  self: SimulationEffect[],
) =>
  self.findIndex(
    (item: SimulationEffect) =>
      item?.campaign?.campaignId === campaign?.campaign?.campaignId,
  ) === index;

export function SimulatedCampaignsTable({
  items,
}: {
  items?: SimulationEffect[];
}) {
  const { message } = useMessage();
  const headItems = createHeadItems([
    {
      headerText: message({ id: "campaignName" }),
      property: "campaignName",
      isSortable: false,
    },
    {
      headerText: message({ id: "campaignType" }),
      property: "campaignType",
      isSortable: false,
    },
    {
      headerText: message({ id: "startDate" }),
      property: "startDate",
      isSortable: false,
    },
    {
      headerText: message({ id: "endDate" }),
      property: "endDate",
      isSortable: false,
    },
  ]);
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const { setColumnOrdering } = useTablesStateHelpers();

  function createItems(items: SimulationEffect[]) {
    return items
      .filter(filterCampaignsWithUniqueID)
      .map(({ campaign }: { campaign?: SimulationCampaign }) =>
        campaign
          ? {
              cells: [
                {
                  content: hasAnyLanguagePermission ? (
                    <LinkItem to={links.editCampaign(campaign.campaignId)}>
                      {campaign.name || "-"}
                    </LinkItem>
                  ) : (
                    campaign.name || "-"
                  ),
                  property: "campaignName",
                },
                { content: campaign.type, property: "campaignType" },
                {
                  content: OLDate(campaign.activity?.startsAt),
                  property: "startDate",
                },
                {
                  content: OLDate(campaign.activity?.endsAt),
                  property: "endDate",
                },
              ],
              id: campaign.campaignId,
            }
          : { cells: [], id: "" },
      );
  }
  const tableItems = items ? createItems(items) : [];
  const { listing } = useListing();
  const navigate = useNavigate();

  return (
    <FormSection
      title={message({ id: "simulatedCampaigns" })}
      noPadding
      noBorder
    >
      <OLTable
        setColumnOrdering={setColumnOrdering}
        disableColumnManager
        tableName={SIMULATE_CAMPAIGN_LIST_ID}
        data-test-id="simulate-campaign"
        sort={{
          order: listing.order,
          property: listing.property,
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editCampaign(`${id}`)),
              disableCondition: () => !hasAnyLanguagePermission,
              label: message({ id: "seeDetails" }),
            },
          ],
        }}
        columns={headItems}
        rows={items && tableItems}
        onSort={() => {}}
      />
    </FormSection>
  );
}
